import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFont, faPlus, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { AdminSectionHeading } from '../../admin-ui';
import type { ThemeSettingsPageStyles } from './themeSettingsPageStyles';
import type { ThemeFontSectionViewModel } from './useThemeFontSettings';
import { FONT_ACCEPT } from './themeSettingsUtils';

export type ThemeFontSectionProps = ThemeFontSectionViewModel & { styles: ThemeSettingsPageStyles; disabled?: boolean; dirty?: boolean };

export function ThemeFontSection({
  styles,
  disabled = false,
  dirty = false,
  fontOptions,
  selectedFontValue,
  fontError,
  fontInputRef,
  fontInputId,
  previewFontStyle,
  onFontSelectChange,
  onFontInputChange,
  onTriggerFontFilePicker,
  onFontUploadKeyDown,
}: ThemeFontSectionProps) {
  return (
    <section style={styles.card} aria-labelledby="theme-settings-font-family">
      <AdminSectionHeading icon={faFont} title="Font Family" headingLevel="h2" titleId="theme-settings-font-family" />
      <div style={styles.fontBody}>
        <div style={styles.fontControls}>
          <div style={styles.fontSelectWrapper}>
            <select
              style={{ ...styles.fontSelect, ...(dirty ? styles.dirtyControl : {}) }}
              value={selectedFontValue}
              onChange={onFontSelectChange}
              aria-label="Font family"
            >
              {fontOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" style={styles.fontSelectChevron} />
          </div>

          <div
            style={styles.fontUploadButton}
            onClick={onTriggerFontFilePicker}
            onKeyDown={onFontUploadKeyDown}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            aria-label="Upload a font file"
          >
            <span style={styles.fontUploadLabel}>Upload a font</span>
            <span style={styles.dropZonePlusIconSm} aria-hidden="true">
              <FontAwesomeIcon icon={faPlus} style={{ fontSize: 12 }} />
            </span>
          </div>

          <input
            ref={fontInputRef}
            id={fontInputId}
            type="file"
            accept={FONT_ACCEPT}
            onChange={onFontInputChange}
            style={styles.visuallyHiddenInput}
            aria-hidden="true"
            tabIndex={-1}
          />

          {fontError ? (
            <p style={styles.errorMessage} role="alert">{fontError}</p>
          ) : null}

          <p style={styles.fontDisclaimer}>
            By uploading a font, you confirm you have the rights to use it on third-party platforms.
          </p>
        </div>

        <div style={styles.fontPreviewCard} aria-label="Font preview">
          <p style={{ ...styles.fontPreviewH1, ...previewFontStyle }}>Headline 1</p>
          <p style={{ ...styles.fontPreviewH2, ...previewFontStyle }}>Headline 2</p>
          <p style={{ ...styles.fontPreviewBody, ...previewFontStyle }}>
            Body copy &mdash; this is how paragraph text will appear to learners throughout the course content.
          </p>
          <p style={{ ...styles.fontPreviewCaption, ...previewFontStyle }}>
            Small copy &mdash; this is how supporting text will appear to learners throughout the course content.
          </p>
        </div>
      </div>
    </section>
  );
}
